import { TextNode, EditorConfig, LexicalNode, NodeKey, BaseSelection, RangeSelection } from "lexical";
import { $isRangeSelection } from "lexical";

export type WordPayload = {
  clipId?: string;
  kind?: string;
  startSec?: number;
  endSec?: number;
  speaker?: string;
  baseStartSec?: number; // first word start for this clip (for relative mapping)
  // Original source times (before playbackRate/offset adjustments) for cutting
  srcStartSec?: number;
  srcEndSec?: number;
  // Which ScriptLayerClip input/transcript this word came from
  sourceIndex?: number;
};

export class WordNode extends TextNode {
  __payload: WordPayload;

  static getType(): string {
    return "word";
  }

  static clone(node: WordNode): WordNode {
    return new WordNode(node.__text, { ...node.__payload }, node.__key);
  }

  constructor(text: string, payload: WordPayload = {}, key?: NodeKey) {
    super(text, key);
    this.__payload = payload;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    // Optionally add data attributes for debugging
    // dom.dataset.word = "1";
    return dom;
  }

  static importJSON(serializedNode: any): WordNode {
    const node = new WordNode(serializedNode.text, serializedNode.payload ?? {});
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): any {
    return {
      ...super.exportJSON(),
      type: "word",
      version: 1,
      payload: this.__payload,
    };
  }

  selectionTransform(_prevSelection: null | BaseSelection, nextSelection: RangeSelection): void {
    if (!$isRangeSelection(nextSelection)) return;

    const wordLen = this.getTextContentSize();

    const normalizePoint = (point: any, shouldSnapToStart: boolean) => {
      if (!point || point.type !== "text") return;
      if (point.key !== this.getKey()) return;
      if (point.offset <= 0 || point.offset >= wordLen) return;
      point.set(this.getKey(), shouldSnapToStart ? 0 : wordLen, "text");
    };

    if (nextSelection.isCollapsed()) {
      // Never allow a collapsed caret inside a word; snap to before-word.
      normalizePoint(nextSelection.anchor, true);
      normalizePoint(nextSelection.focus, true);
      return;
    }

    const isBackward = nextSelection.isBackward();
    // For forward selections: anchor is start, focus is end.
    // For backward selections: anchor is end, focus is start.
    normalizePoint(nextSelection.anchor, isBackward ? false : true);
    normalizePoint(nextSelection.focus, isBackward ? true : false);
  }
}

export function $createWordNode(text: string, payload: WordPayload): WordNode {
  return new WordNode(text, payload);
}

export function $isWordNode(node?: LexicalNode | null): node is WordNode {
  return node instanceof WordNode;
}
